import React, { useCallback, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Checkbox, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { login, loginWithGoogle } from "../../src/container/redux/loginSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "../../src/container/helper/firebase";
import ReCAPTCHA from "react-google-recaptcha";
import Toastify from "../../src/container/helper/Toastify";
import api from "../../src/container/helper/Api";
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
// Remove useNavigate import
import RegistrationPopup from "./RegistrationPopup";

const LoginPopup = ({ isOpen, onClose, onSuccess }) => {
  // Remove navigate declaration

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState(null);
  const [googleErrorMessage, setGoogleErrorMessage] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);

  const schema = yup.object().shape({
    email: yup
      .string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter valid email"
      )
      .required("Please enter valid email"),
    password: yup.string().required("Password is required"),
  });

  const { errors, values, handleChange, touched, handleBlur, handleSubmit, resetForm } =
    useFormik({
      initialValues: {
        email: "",
        password: "",
      },
      validationSchema: schema,
      onSubmit: async (values) => {
        if (!captchaValue) {
          Toastify.error("Please verify the reCAPTCHA");
          return;
        }

        try {
          const { status } = await api.verifyCaptchaToken({
            token: captchaValue,
          });
          if (status === 200) {
            handleLogin(values);
          } else {
            Toastify.error("Invalid request");
          }
        } catch (err) {
          Toastify.error("Invalid request");
        }
      },
    });

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleLogin = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const result = await dispatch(login(values));
        unwrapResult(result);
        Toastify.success("Login successful!");
        onSuccess();
        onClose();
        resetForm();
        setCaptchaValue(null);
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } catch (error) {
        console.error("Login failed:", error);
        Toastify.error("Login failed. Please check your credentials.");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, onSuccess, onClose, resetForm]
  );

  const handleGoogleSignUp = async (e) => {
    e.preventDefault();
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const resultAction = await dispatch(
        loginWithGoogle({
          email: user.email,
          googleIdToken: user.accessToken,
        })
      );

      unwrapResult(resultAction);
      Toastify.success("Login successful!");
      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      const errorMessage = err.message;
      const errorCode = err.code;
      setError(true);

      switch (errorCode) {
        case "auth/operation-not-allowed":
          setGoogleErrorMessage("Email/password accounts are not enabled.");
          break;
        case "auth/operation-not-supported-in-this-environment":
          setGoogleErrorMessage(
            "HTTP protocol is not supported. Please use HTTPS."
          );
          break;
        case "auth/popup-blocked":
          setGoogleErrorMessage(
            "Popup has been blocked by the browser. Please allow popups for this website."
          );
          break;
        case "auth/popup-closed-by-user":
          setGoogleErrorMessage(
            "Popup has been closed by the user before finalizing the operation. Please try again."
          );
          break;
        default:
          setGoogleErrorMessage(errorMessage);
          break;
      }
      Toastify.error(googleErrorMessage || "Google sign-in failed");
    }
  };

  const handleForgotPasswordClick = () => {
    onClose(); // Just close the popup, no navigation
    // Remove navigate("/forgotpassword");
  };

  const handleRegisterClick = () => {
    setShowRegistrationPopup(true); // Open registration popup
  };

  const handleRegistrationClose = () => {
    setShowRegistrationPopup(false);
  };

  const handleRegistrationSuccess = () => {
    setShowRegistrationPopup(false);
    onClose(); // Close login popup too
    Toastify.success("Registration successful! Please login.");
  };

  // Don't render anything if neither popup should be shown
  if (!isOpen && !showRegistrationPopup) return null;

  const popupStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    content: {
      backgroundColor: "white",
      padding: "30px",
      borderRadius: "10px",
      width: "90%",
      maxWidth: "450px",
      position: "relative",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    closeButton: {
      position: "absolute",
      top: "10px",
      right: "10px",
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#666",
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      marginBottom: "20px",
      color: "#333",
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "500",
      color: "#555",
    },
    inputBox: {
      display: "flex",
      alignItems: "center",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "12px 15px",
      marginBottom: "5px",
      backgroundColor: "#fafafa",
    },
    input: {
      flex: 1,
      border: "none",
      outline: "none",
      backgroundColor: "transparent",
      fontSize: "14px",
    },
    passwordToggle: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      color: "#666",
    },
    error: {
      color: "#f44336",
      fontSize: "12px",
      marginBottom: "10px",
      marginTop: "0",
    },
    captcha: {
      margin: "15px 0",
      transform: "scale(0.9)",
      transformOrigin: "0 0",
    },
    forgotText: {
      color: "#1D83F8",
      cursor: "pointer",
      textAlign: "right",
      margin: "10px 0",
      fontSize: "14px",
    },
    actions: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "15px 0",
    },
    remember: {
      display: "flex",
      alignItems: "center",
    },
    submitBtn: {
      backgroundColor: "#1D83F8",
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    submitBtnDisabled: {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    social: {
      margin: "20px 0",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      margin: "15px 0",
    },
    line: {
      flex: 1,
      height: "1px",
      backgroundColor: "#e0e0e0",
    },
    dividerText: {
      margin: "0 10px",
      color: "#999",
      fontSize: "14px",
    },
    googleBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      border: "1px solid #e0e0e0",
      padding: "12px",
      borderRadius: "8px",
      cursor: "pointer",
      backgroundColor: "#fafafa",
      transition: "background-color 0.2s",
    },
    registerLink: {
      textAlign: "center",
      marginTop: "15px",
      fontSize: "14px",
      color: "#666",
    },
    registerSpan: {
      color: "#1D83F8",
      cursor: "pointer",
      fontWeight: "500",
      marginLeft: "5px",
    },
  };

  return (
    <>
      {/* Login Popup */}
      {isOpen && (
        <div style={popupStyles.overlay}>
          <div style={popupStyles.content}>
            <button style={popupStyles.closeButton} onClick={onClose}>
              ×
            </button>

            <h3 style={popupStyles.title}>Sign in to your account</h3>

            <div>
              <label style={popupStyles.label}>Email</label>
              <div style={popupStyles.inputBox}>
                <input
                  type="email"
                  onBlur={handleBlur}
                  value={values.email}
                  placeholder="Enter your email"
                  name="email"
                  onChange={handleChange}
                  style={popupStyles.input}
                />
              </div>
              {errors.email && touched.email && (
                <p style={popupStyles.error}>{errors.email}</p>
              )}
            </div>

            <div>
              <label style={popupStyles.label}>Password</label>
              <div style={popupStyles.inputBox}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  onBlur={handleBlur}
                  value={values.password}
                  placeholder="Enter your password"
                  name="password"
                  onChange={handleChange}
                  style={popupStyles.input}
                />
                <div
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={popupStyles.passwordToggle}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </div>
              </div>
              {errors.password && touched.password && (
                <p style={popupStyles.error}>{errors.password}</p>
              )}
            </div>

            <div style={popupStyles.captcha}>
              <ReCAPTCHA
                sitekey="6Lc8vBcrAAAAANcniDVhDJlYjI_dim7gFk3L4u19"
                onChange={handleCaptchaChange}
              />
            </div>

            <div style={popupStyles.forgotText} onClick={handleForgotPasswordClick}>
              Forgot Password?
            </div>

            <div style={popupStyles.actions}>
              <div style={popupStyles.remember}>
                <Checkbox />
                <span>Remember me</span>
              </div>
              <button
                style={{
                  ...popupStyles.submitBtn,
                  ...(loading ? popupStyles.submitBtnDisabled : {}),
                }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} thickness={4.5} sx={{ color: "#fff" }} />
                ) : (
                  <>
                    Sign In <ArrowRightIcon className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>

            <div style={popupStyles.social}>
              <div style={popupStyles.divider}>
                <div style={popupStyles.line}></div>
                <div style={popupStyles.dividerText}>SIGN IN WITH</div>
                <div style={popupStyles.line}></div>
              </div>
              <div style={popupStyles.googleBtn} onClick={handleGoogleSignUp}>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google
              </div>
            </div>

            <div style={popupStyles.registerLink}>
              New at Kingster?{" "}
              <span style={popupStyles.registerSpan} onClick={handleRegisterClick}>
                Register here
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Registration Popup - rendered separately */}
      <RegistrationPopup
        isOpen={showRegistrationPopup}
        onClose={handleRegistrationClose}
        onSuccess={handleRegistrationSuccess}
      />
    </>
  );
};

export default LoginPopup;